/**
 * Binds the sandbox uploader to the live AHP relay for a task.
 *
 * Returns `available: false` whenever a write is impossible — no relay, a read-only
 * (passive) connection, or a host that never reported a working directory. Callers treat
 * that as "inline attachments as before", which is the correct behaviour for surfaces
 * with no sandbox at all (plain Copilot Chat) and for observers.
 */

import {useCallback, useMemo, useRef, useSyncExternalStore} from 'react'
import {
  getRelayHostInfo,
  getRelayResourceWriter,
  isRelayPassive,
  subscribeRelayRegistry,
  type SandboxResourceWriter,
} from '@github-ui/ahp-relay/relay-registry'

import {
  type SandboxWriter,
  type UploadedFile,
  type UploadProgress,
  uploadFileToSandbox,
} from '../attachments/upload-to-sandbox'

/**
 * Directory uploads land in, relative to the host's working directory.
 *
 * Grouped per day and session so repeated uploads of the same filename across sessions
 * don't collide, and so the directory stays greppable and easy to ignore in git.
 */
export function uploadDirectoryFor(sessionId: string | undefined, now: Date = new Date()): string {
  const day = now.toISOString().slice(0, 10)
  const shortId = (sessionId ?? 'session').replace(/[^A-Za-z0-9-]/g, '').slice(0, 8) || 'session'
  return `.copilot/uploads/${day}-${shortId}`
}

export interface SandboxUploadHandle {
  available: boolean
  /** Absolute directory uploads land in, for display. `undefined` when unavailable. */
  directory: string | undefined
  upload: (options: {
    file: File
    fallbackName: string
    signal?: AbortSignal
    onProgress?: (progress: UploadProgress) => void
  }) => Promise<UploadedFile>
  /**
   * Deletes an already-written upload. Used when the user removes an attachment that
   * finished uploading: dropping the attachment alone would leave the bytes on disk,
   * where the agent can still list and read them.
   */
  remove: (uri: string) => Promise<void>
}

export function useSandboxUpload({
  taskId,
  sessionId,
  enabled = true,
}: {
  taskId: string | undefined
  sessionId: string | undefined
  enabled?: boolean
}): SandboxUploadHandle {
  const directoryPath = useMemo(() => uploadDirectoryFor(sessionId), [sessionId])

  // Subscribe to the primitive the writer depends on rather than the writer object:
  // `getRelayResourceWriter` builds a fresh object each call, which would make
  // `useSyncExternalStore` loop forever on an unstable snapshot.
  const hostDirectory = useSyncExternalStore(
    subscribeRelayRegistry,
    () => (enabled && !isRelayPassive(taskId) ? getRelayHostInfo(taskId)?.defaultDirectory : undefined),
    () => undefined,
  )

  // Read through on every call rather than caching the writer: the relay reconnects
  // under us, and a writer captured at render time would keep a disposed client.
  const readWriter = useCallback(
    (): SandboxResourceWriter | undefined => (enabled ? getRelayResourceWriter(taskId, directoryPath) : undefined),
    [enabled, taskId, directoryPath],
  )

  const ensuredDirectoryRef = useRef<string | null>(null)

  const upload = useCallback(
    async ({
      file,
      fallbackName,
      signal,
      onProgress,
    }: {
      file: File
      fallbackName: string
      signal?: AbortSignal
      onProgress?: (progress: UploadProgress) => void
    }) => {
      const resourceWriter = readWriter()
      if (!resourceWriter) throw new Error('File uploads are not available for this cloud session')

      // `resourceWrite` fails with NotFound when the parent is missing, so create the
      // directory once per relay. `resourceMkdir` creates parents and is idempotent.
      if (ensuredDirectoryRef.current !== resourceWriter.directory) {
        await resourceWriter.mkdir(resourceWriter.directory)
        ensuredDirectoryRef.current = resourceWriter.directory
      }

      const writer: SandboxWriter = {
        resolve: resourceWriter.resolve,
        mkdir: resourceWriter.mkdir,
        remove: resourceWriter.remove,
        stat: resourceWriter.stat?.bind(resourceWriter),
        write: params => resourceWriter.write(params),
      }

      return uploadFileToSandbox({writer, file, fallbackName, signal, onProgress})
    },
    [readWriter],
  )

  const remove = useCallback(
    async (uri: string) => {
      await readWriter()?.remove(uri)
    },
    [readWriter],
  )

  return useMemo(
    () => ({
      available: hostDirectory !== undefined,
      directory: hostDirectory === undefined ? undefined : `${hostDirectory}/${directoryPath}`,
      upload,
      remove,
    }),
    [hostDirectory, directoryPath, upload, remove],
  )
}
